import React from 'react';
import { logger } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface RouteErrorBoundaryProps {
    /** Human-readable name of the route/section, surfaced in logs and the fallback UI. */
    routeName: string;
    children: React.ReactNode;
    fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface RouteErrorBoundaryState {
    error: Error | null;
}

/**
 * Lightweight class-based Error Boundary used to isolate route-level crashes so
 * one broken page does not take down the entire SPA. Logs to the shared logger
 * (which forwards to CloudWatch in production) and renders a recoverable fallback.
 */
export class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
    state: RouteErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        logger.error(`Unhandled error in route "${this.props.routeName}"`, error, {
            componentStack: info.componentStack,
        });
    }

    private reset = () => {
        this.setState({ error: null });
    };

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        if (this.props.fallback) {
            return this.props.fallback(error, this.reset);
        }

        return (
            <div
                role="alert"
                className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-8 text-center"
            >
                <AlertTriangle className="w-12 h-12 text-destructive" aria-hidden="true" />
                <div>
                    <h2 className="text-xl font-semibold">Something went wrong</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                        The {this.props.routeName} page encountered an unexpected error.
                        You can try again, or refresh the page if the problem persists.
                    </p>
                </div>
                {process.env.NODE_ENV === 'development' && (
                    <pre className="text-xs text-left bg-muted p-3 rounded max-w-2xl overflow-auto">
                        {error.message}
                    </pre>
                )}
                <div className="flex gap-2">
                    <Button variant="default" onClick={this.reset}>Try again</Button>
                    <Button variant="outline" onClick={() => window.location.reload()}>Reload page</Button>
                </div>
            </div>
        );
    }
}

export default RouteErrorBoundary;
